import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '@/services/api';

// Types
export interface Audit {
  id: number;
  website_url: string;
  website_name?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  organization: number;
  project: number;
  created_at: string;
  updated_at: string;
  audit_data?: any;
}

export interface Issue {
  id: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  url: string;
  element: string;
  recommendation: string;
  status: 'pending' | 'fixed' | 'ignored';
  created_at: string;
  updated_at?: string;
}

export interface AuditState {
  audits: Audit[];
  currentAudit: Audit | null;
  issues: Issue[];
  isLoading: boolean;
  isFixing: boolean;
  fixProgress: number;
  error: string | null;
}

const initialState: AuditState = {
  audits: [],
  currentAudit: null,
  issues: [],
  isLoading: false,
  isFixing: false,
  fixProgress: 0,
  error: null,
};

// Async Thunks
export const createAudit = createAsyncThunk(
  'audit/createAudit',
  async (auditData: { website_url: string; organization: number; project: number }) => {
    const response = await apiService.post('/website_audit/', auditData);
    return response.data;
  }
);

export const getAudits = createAsyncThunk(
  'audit/getAudits',
  async (params?: { organization_id?: number; project_id?: number; status?: string }) => {
    const response = await apiService.get('/website_audit/', { params });
    return response.data;
  }
);

export const getAudit = createAsyncThunk(
  'audit/getAudit',
  async (auditId: number) => {
    const response = await apiService.get(`/website_audit/${auditId}/`);
    return response.data;
  }
);

export const getAuditProgress = createAsyncThunk(
  'audit/getAuditProgress',
  async (auditId: number) => {
    const response = await apiService.get(`/website_audit/${auditId}/progress/`);
    return response.data;
  }
);

export const getAuditIssues = createAsyncThunk(
  'audit/getAuditIssues',
  async (auditId: number, { getState }) => {
    const response = await apiService.get(`/website_audit/${auditId}/issues/`);
    return response.data;
  }
);

export const updateAudit = createAsyncThunk(
  'audit/updateAudit',
  async ({ auditId, data }: { auditId: number; data: Partial<Audit> }) => {
    const response = await apiService.patch(`/website_audit/${auditId}/`, data);
    return response.data;
  }
);

export const deleteAudit = createAsyncThunk(
  'audit/deleteAudit',
  async (auditId: number) => {
    await apiService.delete(`/website_audit/${auditId}/`);
    return auditId;
  }
);

export const fixAllIssues = createAsyncThunk(
  'audit/fixAllIssues',
  async (auditId: number, { dispatch }) => {
    // Simulate fixing all issues with progress updates
    return new Promise<{ success: boolean; fixedCount: number }>((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        dispatch(setFixProgress(progress));
        
        if (progress >= 100) {
          clearInterval(interval);
          resolve({ success: true, fixedCount: 10 }); // Mock count
        }
      }, 500);
    });
  }
);

export const fixSingleIssue = createAsyncThunk(
  'audit/fixSingleIssue',
  async (issueId: number, { dispatch, getState }) => {
    // Simulate fixing single issue
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update issue status
    const response = await apiService.patch(`/website_audit/issues/${issueId}/`, {
      status: 'fixed'
    });
    
    return response.data;
  }
);

export const getAuditStats = createAsyncThunk(
  'audit/getAuditStats',
  async (organizationId?: number) => {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await apiService.get('/website_audit/stats/', { params });
    return response.data;
  }
);

export const getIssueCategories = createAsyncThunk(
  'audit/getIssueCategories',
  async () => {
    const response = await apiService.get('/website_audit/categories/');
    return response.data;
  }
);

// Slice
const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    setCurrentAudit: (state, action: PayloadAction<Audit | null>) => {
      state.currentAudit = action.payload;
    },
    setFixProgress: (state, action: PayloadAction<number>) => {
      state.fixProgress = action.payload;
    },
    setFixing: (state, action: PayloadAction<boolean>) => {
      state.isFixing = action.payload;
    },
    updateIssueStatus: (state, action: PayloadAction<{ issueId: number; status: string }>) => {
      const issue = state.issues.find(issue => issue.id === action.payload.issueId);
      if (issue) {
        issue.status = action.payload.status as 'pending' | 'fixed' | 'ignored';
      }
    },
    resetAuditState: (state) => {
      state.currentAudit = null;
      state.issues = [];
      state.isFixing = false;
      state.fixProgress = 0;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Create Audit
    builder
      .addCase(createAudit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAudit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAudit = action.payload as unknown as Audit;
        state.audits.unshift(action.payload as unknown as Audit);
      })
      .addCase(createAudit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create audit';
      });

    // Get Audits
    builder
      .addCase(getAudits.fulfilled, (state, action) => {
        const payload = action.payload as unknown as { results: Audit[]; count: number };
        state.audits = payload.results || payload;
      });

    // Get Audit
    builder
      .addCase(getAudit.fulfilled, (state, action) => {
        state.currentAudit = action.payload as unknown as Audit;
      });

    // Get Audit Progress
    builder
      .addCase(getAuditProgress.fulfilled, (state, action) => {
        if (state.currentAudit) {
          const payload = action.payload as any;
          state.currentAudit.status = payload.status as 'pending' | 'in_progress' | 'completed' | 'failed';
          state.currentAudit.updated_at = payload.updated_at;
        }
      });

    // Get Audit Issues
    builder
      .addCase(getAuditIssues.fulfilled, (state, action) => {
        const payload = action.payload as unknown as { results: Issue[]; count: number };
        state.issues = payload.results || payload;
      });

    // Fix All Issues
    builder
      .addCase(fixAllIssues.pending, (state) => {
        state.isFixing = true;
        state.fixProgress = 0;
      })
      .addCase(fixAllIssues.fulfilled, (state, action) => {
        state.isFixing = false;
        state.fixProgress = 100;
        // Update all issues to fixed status
        state.issues.forEach(issue => {
          issue.status = 'fixed';
        });
      })
      .addCase(fixAllIssues.rejected, (state, action) => {
        state.isFixing = false;
        state.error = action.error.message || 'Failed to fix all issues';
      });

    // Fix Single Issue
    builder
      .addCase(fixSingleIssue.pending, (state, action) => {
        const issue = state.issues.find(issue => issue.id === action.meta.arg);
        if (issue) {
          issue.status = 'fixed';
        }
      })
      .addCase(fixSingleIssue.fulfilled, (state, action) => {
        // Issue status already updated in pending
      })
      .addCase(fixSingleIssue.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fix issue';
      });

    // Update Audit
    builder
      .addCase(updateAudit.fulfilled, (state, action) => {
        const payload = action.payload as unknown as Audit;
        if (state.currentAudit && state.currentAudit.id === payload.id) {
          state.currentAudit = payload;
        }
        const index = state.audits.findIndex(audit => audit.id === payload.id);
        if (index !== -1) {
          state.audits[index] = payload;
        }
      });

    // Delete Audit
    builder
      .addCase(deleteAudit.fulfilled, (state, action) => {
        state.audits = state.audits.filter(audit => audit.id !== action.payload);
        if (state.currentAudit?.id === action.payload) {
          state.currentAudit = null;
        }
      });
  }
});

export const {
  setCurrentAudit,
  setFixProgress,
  setFixing,
  updateIssueStatus,
  resetAuditState,
  clearError
} = auditSlice.actions;

export default auditSlice.reducer;
